import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Loader from 'components/Loader'
import { RowBetween, RowFixed } from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { TruncatedText } from 'components/swap/styleds'
import { MouseoverTooltip } from 'components/Tooltip'
import { useContext } from 'react'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'

type WithdrawFromBaultStepProps = {
  amount: CurrencyAmount<Token> | undefined
  baultAddress: string | undefined
  estimatedLp: CurrencyAmount<Token> | undefined
  exitFee: string
  token0?: Token | null
  token1?: Token | null
  next: () => void
  onRedeem: () => void
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

const WithdrawFromBaultStep = ({
  amount,
  baultAddress,
  estimatedLp,
  exitFee,
  token0,
  token1,
  onRedeem,
  next,
  attemptingTxn,
  ...rest
}: WithdrawFromBaultStepProps) => {
  const theme = useContext(ThemeContext)

  // Only render if there's an amount to withdraw
  if (!amount) {
    return null
  }

  return (
    <ConfirmationTransactionStep onConfirm={next} attemptingTxn={attemptingTxn} {...rest}>
      <Column width="100%" gap="4px" style={{ marginTop: '12px' }}>
        {/* Bault Shares Amount Card */}
        <LightCard padding="0.75rem 1rem">
          <AutoColumn gap="8px">
            <RowBetween align="center">
              <RowFixed gap="0px">
                <TruncatedText fontSize={24} fontWeight={500}>
                  {amount ? amount.toExact() : <Loader />}
                </TruncatedText>
              </RowFixed>
              <RowFixed gap="0px">
                <CurrencyLogo currency={amount.currency} size="20px" style={{ marginRight: '12px' }} />
                <Text fontSize={20} fontWeight={500}>
                  {amount.currency.symbol}
                </Text>
              </RowFixed>
            </RowBetween>
            <RowBetween>
              <FiatValue currencyAmount={amount} />
            </RowBetween>
          </AutoColumn>
        </LightCard>

        {/* Expected LP Tokens and Details Card */}
        <LightCard>
          <AutoColumn gap="8px">
            <RowBetween>
              <RowFixed>
                <MouseoverTooltip
                  text={<>The LP tokens you'll receive when redeeming your Bault shares.</>}
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>
                    You Will Receive
                  </ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <RowFixed>
                <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                  {estimatedLp ? estimatedLp.toSignificant(6) : '-'} {estimatedLp?.currency.symbol || 'LP'}
                </ThemedText.DeprecatedBlack>
              </RowFixed>
            </RowBetween>
          </AutoColumn>
        </LightCard>

        <ButtonPrimary
          style={{ margin: '20px 0 0 0' }}
          onClick={onRedeem}
          disabled={!amount || attemptingTxn || amount.equalTo('0')}
        >
          <Text fontWeight={500} fontSize={20}>
            <>Confirm Redemption</>
          </Text>
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default WithdrawFromBaultStep
